/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import { Card, Icons } from '../../../../bphComponents/bundle';
import COLORS from '../../../styles/Colors';
import FlexRow from '../../../components/FlexRow';
import { FlagToggleHorizontal } from '../../../components/FlagToggle';
import HealthEntryCategoryNames from '../../../../constants/HealthEntryCategoryNames';
import HealthEntryModel from '../../../../models/HealthEntry';
import DeleteOrReconsiderWarningModal from '../../../components/modal/DeleteOrReconsiderWarningModal';
import { useActiveProfile } from '../../../hooks/useProfile';
import { useUIState } from '../../../hooks/useUIState';
import ProfileModel from '../../../../models/ProfileModel';
import { iTrackable, iObservation } from '../../../../apiServices/apiModels/Trackable';

const getReconsiderPhrase = (category, isMainProfile, profileName) => {
  let someCategory = '';

  switch (category) {
    case HealthEntryCategoryNames.condition:
      someCategory = 'conditions';
      break;
    case HealthEntryCategoryNames.allergy:
      someCategory = 'allergies';
      break;
    case HealthEntryCategoryNames.medication:
      someCategory = 'medications';
      break;
    case HealthEntryCategoryNames.otherTreatment:
      someCategory = 'treatments';
      break;
    default:
      break;
  }

  return {
    title: {
      id: `heDeleteConfirm.makePast.${someCategory}.heading`,
    },
    body: isMainProfile ? {
      id: 'heDeleteConfirm.makePast.user.message.pre',
    } : {
      id: 'heDeleteConfirm.makePast.notUser.message.pre',
      values: {
        string1: profileName,
      },
    },
    button: {
      id: 'heDeleteConfirm.makePast.button.label',
    },
    alternateButton: {
      id: 'heDeleteConfirm.delete.button.label',
    },
    cancel: {
      id: 'nav.cancel.button.label',
    },
  };
};

interface Props {
  onToggleFlag(): void;
  onDelete?(): void;
  onToggleIsCurrent?(): void;
  altLeftContent?: any;
  primaryButtonPhrase?: any;
  isFlagged: boolean;
  shouldReconsiderDelete: boolean;
  reconsiderPhraseMessages: any;
}

const OccurrenceFooter: React.FC<Props> = (props) => {
  const { onDelete, onToggleFlag, altLeftContent, primaryButtonPhrase, onToggleIsCurrent, isFlagged, shouldReconsiderDelete, reconsiderPhraseMessages } = props;
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const showFlagged = Boolean(onToggleFlag);

  return (
    <Card.ExternalContent>
      <FlexRow>
        <div>
          {
            altLeftContent
            ? altLeftContent
            : showFlagged
            ? <FlagToggleHorizontal isFlagged={isFlagged} onClick={onToggleFlag} colorTheme="gsMedDark" />
            : null
          }
        </div>

        {
          onDelete
          ? (
            <div
              className="is-clickable"
              onClick={() => {
                setShowDeleteModal(true);
              }}
            >
              <Icons.IconTrash.Medium color={COLORS.gsMiddle} />
            </div>
          ) : <div />
        }
      </FlexRow>

      <DeleteOrReconsiderWarningModal
        isVisible={showDeleteModal}
        shouldReconsider={shouldReconsiderDelete}
        deleteModal={{
          phraseMessages: {
            primaryButton: primaryButtonPhrase,
          },
          onConfirm: () => {
            setShowDeleteModal(false);
            onDelete();
          },
          onCancel: () => {
            setShowDeleteModal(false);
          },
        }}
        reconsiderModal={{
          phraseMessages: reconsiderPhraseMessages,
          onConfirm: () => {
            if (onToggleIsCurrent) {
              onToggleIsCurrent();
            }
            setShowDeleteModal(false);
            window.scrollTo(0, 0);
          },
          onAlternate: () => {
            onDelete();
            setShowDeleteModal(false);
          },
          onCancel: () => {
            setShowDeleteModal(false);
          },
        }}
      />
    </Card.ExternalContent>
  );
};

interface TrackableFooterProps {
  trackable: iTrackable;
  onToggleFlag(): void;
  onDelete?(): void;
}
export const TrackableFooter: React.FC<TrackableFooterProps> = (props) => {
  const { onDelete, onToggleFlag, trackable } = props;

  return (
    <OccurrenceFooter
      onDelete={onDelete}
      onToggleFlag={onToggleFlag}
      primaryButtonPhrase={{
        id: 'heSummary.deleteConfirm.button.label',
      }}
      isFlagged={trackable.flagged}
      altLeftContent={null}
      onToggleIsCurrent={null}
      shouldReconsiderDelete={false}
      reconsiderPhraseMessages={{}}
    />
  );
};

interface ObservationFooterProps {
  observation: iObservation;
  onToggleFlag(): void;
  onDelete?(): void;
}
export const ObservationFooter: React.FC<ObservationFooterProps> = (props) => {
  const { onDelete, onToggleFlag, observation } = props;

  return (
    <OccurrenceFooter
      onDelete={onDelete}
      onToggleFlag={onToggleFlag}
      primaryButtonPhrase={{
        id: 'delete.deleteConfirm.button.label',
      }}
      isFlagged={observation.flagged}
      altLeftContent={null}
      onToggleIsCurrent={null}
      shouldReconsiderDelete={false}
      reconsiderPhraseMessages={{}}
    />
  );
};

export default OccurrenceFooter;
